/* eslint-disable @typescript-eslint/naming-convention */
import { Injectable } from '@angular/core';
import { GasSensorClassification, SensorClassification, SensorDataChartTypes, SensorTypes, SpeedSensorClassification } from '@app/shared/enums';
import { isNil } from 'lodash';
import { defaultSpeedLimit } from '../shared/constants';
import { SpeedLimitDto } from './models/speed-limit';
import { Sensor } from './models/sensor';

const datasetLabels = {
  externalTemp: 'Lufttemperatur',
  internalTemp: 'Straßentemperatur',
  humidity: 'rel. Luftfeuchte',
  temperature: 'Temperatur',
  dioxide: 'CO2',
  monoxide: 'CO',
  particulate: 'Feinstaub',
  motion: 'Zähler',
  fluid: 'Ø Freiraum (l)',
  smoke: 'Zustand Rauchalarm',
  speed: '',
  water: 'Verbrauch [m³]',
  heat: 'Verbrauch [kWh]',
  gasEstimated: 'Verbrauch [kWh]',
  gasCalculated: 'Verbrauch [m³]',
  electricity: 'Verbrauch [kWh]',
  dogToilet: 'Tütenspender / Mülleimer (%)',
  dogBags: 'Tütenspender',
  dogTrashCan: 'Mülleimer',
  moisture: 'Feuchteindikator',
};

const units = {
  percentage: '%',
  temperature: '°C',
  co2: 'CO2/ppm',
  co: 'CO/ppm',
  particulate: 'µg/m³',
  permittivity: 'εr',
};

const defaultChartColors = 'chartColors';

@Injectable({
  providedIn: 'root',
})
export class SensorSettings {
  selectedSensor?: Sensor;
  settings: ISettings[] = [];

  constructor() {
    this.loadSettings();
  }

  public loadSettings(): void {
    this.settings = [
      {
        type: SensorTypes.TEMPERATURE,
        properties: `
          temperature_1
          temperature_2
        `,
        interfaces: `alarmRules {
          ruleJSON
        }`,
        details: [
          {
            classification: SensorClassification.NONE,
            properties: null,
            yAxisLabels: [this.generateLabel(0, 'left', 'Ø Temperatur (°C)', 0.3)],
            datasetLabels: {
              temperature_1: datasetLabels.externalTemp,
              temperature_2: datasetLabels.internalTemp,
            },
            colors: defaultChartColors,
            stacked: false,
          },
        ],
      },
      {
        type: SensorTypes.CARBONDIOXIDE,
        properties: `
          humidity_1
          temperature_1
          co2_1
        `,
        interfaces: `qualityIndices
                    currentQuality
                    criticalQualityIndex`,
        details: [
          {
            classification: SensorClassification.NONE,
            properties: null,
            yAxisLabels: [
              this.generateLabel(0, 'right', `${datasetLabels.humidity} (${units.percentage})`, 0.3),
              this.generateLabel(1, 'right', `${datasetLabels.temperature} (${units.temperature})`, 0.3),
              this.generateLabel(2, 'left', `${datasetLabels.dioxide} (${units.co2})`, 0.3),
            ],
            datasetLabels: {
              humidity_1: datasetLabels.humidity,
              temperature_1: datasetLabels.temperature,
              co2_1: datasetLabels.dioxide,
            },
            colors: defaultChartColors,
            stacked: false,
          },
        ],
      },
      {
        type: SensorTypes.CARBONMONOXIDE,
        properties: `... on sensorReading {
          humidity
          temperature
          monoxide
          batteryLevel
        }`,
        details: [
          {
            classification: SensorClassification.NONE,
            properties: null,
            yAxisLabels: [
              this.generateLabel(0, 'right', `${datasetLabels.humidity} (${units.percentage})`, 0.3),
              this.generateLabel(1, 'right', `${datasetLabels.temperature} (${units.temperature})`, 0.3),
              this.generateLabel(2, 'left', `${datasetLabels.monoxide} (${units.co})`, 0.3),
            ],
            datasetLabels: {
              humidity: datasetLabels.humidity,
              temperature: datasetLabels.temperature,
              monoxide: datasetLabels.monoxide,
            },
            colors: defaultChartColors,
            stacked: false,
          },
        ],
      },
      {
        type: SensorTypes.PARTICULATE,
        properties: `... on sensorReading {
          humidity
          temperature
          particles
          batteryLevel
        }`,
        details: [
          {
            classification: SensorClassification.NONE,
            properties: null,
            yAxisLabels: [
              this.generateLabel(0, 'right', `${datasetLabels.humidity} (${units.percentage})`, 0.3),
              this.generateLabel(1, 'right', `${datasetLabels.temperature} (${units.temperature})`, 0.3),
              this.generateLabel(2, 'left', `${datasetLabels.particulate} (${units.particulate})`, 0.3),
            ],
            datasetLabels: {
              humidity: datasetLabels.humidity,
              temperature: datasetLabels.temperature,
              particles: datasetLabels.particulate,
            },
            colors: defaultChartColors,
            stacked: false,
          },
        ],
      },
      {
        type: SensorTypes.MOTION,
        properties: `... on sensorReading{
          motionCount,
          motionDayCount,
          motionWeekCount,
          motionMonthCount,
          batteryLevel
        }`,
        details: [
          {
            classification: SensorDataChartTypes.monthly,
            properties: null,
            yAxisLabels: [this.generateLabel(0, 'left', datasetLabels.motion, 0.3)],
            datasetLabels: {
              motionMonthCount: '',
            },
            colors: defaultChartColors,
            stacked: false,
          },
          {
            classification: SensorDataChartTypes.daily,
            properties: null,
            yAxisLabels: [this.generateLabel(0, 'left', datasetLabels.motion, 0.3)],
            datasetLabels: {
              motionDayCount: '',
            },
            colors: defaultChartColors,
            stacked: false,
          },
          {
            classification: SensorDataChartTypes.weekly,
            properties: null,
            yAxisLabels: [this.generateLabel(0, 'left', datasetLabels.motion, 0.3)],
            datasetLabels: {
              motionDayCount: '',
            },
            colors: defaultChartColors,
            stacked: false,
          },
          {
            classification: SensorClassification.NONE,
            properties: null,
            yAxisLabels: [this.generateLabel(0, 'left', datasetLabels.motion, 0.3)],
            datasetLabels: {
              motionCount: '',
            },
            colors: defaultChartColors,
            stacked: false,
          },
        ],
      },
      {
        type: SensorTypes.AIRQUALITY,
        properties: `... on sensorReading{
          humidity_1
          temperature_1
        }`,
        details: [
          {
            classification: SensorClassification.NONE,
            properties: null,
            yAxisLabels: [
              this.generateLabel(0, 'right', `${datasetLabels.humidity} (${units.percentage})`, 0.3),
              this.generateLabel(1, 'right', `${datasetLabels.temperature} (${units.temperature})`, 0.3),
            ],
            datasetLabels: {
              humidity_1: datasetLabels.humidity,
              temperature_1: datasetLabels.temperature,
            },
            colors: defaultChartColors,
            stacked: false,
          },
        ],
      },
      {
        type: SensorTypes.FLUID,
        properties: `... on tankReading{
          ullage
          batteryLevel
        }`,
        interfaces: `... on TankSensor {
          Diameter
          Height
          Length
          SideLength
          TankType
          Width
        }`,
        details: [
          {
            classification: SensorClassification.NONE,
            properties: null,
            yAxisLabels: [this.generateLabel(1, 'left', datasetLabels.fluid, 0.3)],
            datasetLabels: {
              ullage: datasetLabels.fluid,
            },
            colors: defaultChartColors,
            stacked: false,
          },
        ],
      },
      {
        type: SensorTypes.SPEED,
        properties: `
          speed_1
        `,
        interfaces: ``,
        details: [
          {
            classification: SpeedSensorClassification.DIRECTION,
            properties: `... on sensorReading {
              speed_avg_1
              speed_1

              cars_count_less_than_30_inbound_1
              cars_count_between_30_and_50_inbound_1
              cars_count_between_50_and_60_inbound_1
              cars_count_between_60_and_80_inbound_1
              cars_count_between_80_and_100_inbound_1
              cars_count_between_100_and_120_inbound_1
              cars_count_above_120_inbound_1
              cars_count_less_than_30_outbound_1
              cars_count_between_30_and_50_outbound_1
              cars_count_between_50_and_60_outbound_1
              cars_count_between_60_and_80_outbound_1
              cars_count_between_80_and_100_outbound_1
              cars_count_between_100_and_120_outbound_1
              cars_count_above_120_outbound_1
            }`,
            yAxisLabels: [this.generateLabel(0, 'left', 'Anzahl Fahrzeuge')],
            datasetLabels: {
              cars_count_less_than_30_inbound_1: '< 30 km/h',
              cars_count_between_30_and_50_inbound_1: '30 - 50 km/h',
              cars_count_between_50_and_60_inbound_1: '50 - 60 km/h',
              cars_count_between_60_and_80_inbound_1: '60 - 80 km/h',
              cars_count_between_80_and_100_inbound_1: '80 - 100 km/h',
              cars_count_between_100_and_120_inbound_1: '100 - 120 km/h',
              cars_count_above_120_inbound_1: '> 120 km/h',
              cars_count_less_than_30_outbound_1: '< 30 km/h',
              cars_count_between_30_and_50_outbound_1: '30 - 50 km/h',
              cars_count_between_50_and_60_outbound_1: '50 - 60 km/h',
              cars_count_between_60_and_80_outbound_1: '60 - 80 km/h',
              cars_count_between_80_and_100_outbound_1: '80 - 100 km/h',
              cars_count_between_100_and_120_outbound_1: '100 - 120 km/h',
              cars_count_above_120_outbound_1: '> 120 km/h',
            },
            colors: 'stackedChartColors',
            stacked: true,
          },
          {
            classification: SpeedSensorClassification.CAR_SIZE,
            properties: `... on sensorReading {
              speed_avg_1
              speed_1
              small_car_count_1
              medium_car_count_1
              large_car_count_1
              xlarge_car_count_1
              total_car_count_1
            }`,
            yAxisLabels: [this.generateLabel(0, 'left', 'Anzahl Fahrzeuge')],
            datasetLabels: {
              small_car_count_1: 'Kleinwagen',
              medium_car_count_1: 'Mittelklasse',
              large_car_count_1: 'SUV - Limousine',
              xlarge_car_count_1: 'LKW',
            },
            colors: 'carSizeChartColors',
            stacked: false,
          },
          {
            classification: SpeedSensorClassification.SPEED_LIMIT,
            properties: `... on sensorReading {
              speed_avg_1
              speed_max_1
              speed_1
              cars_zone_${this.getCurrentSpeedLimit()}_count
            }`,
            yAxisLabels: [this.generateLabel(0, 'left', 'Anzahl Fahrzeuge')],
            datasetLabels: {
              [`carsCountRecommendedSpeed`]: 'Richtgeschwindigkeit',
              [`carsCountTolerableSpeed`]: 'Toleranzrahmen',
              [`carsCountOverSpeed`]: 'Tempoüberschreitung',
            },
            colors: 'carSpeedLimitChartColors',
            stacked: true,
          },
        ],
      },
      {
        type: SensorTypes.SMOKE,
        properties: `... on smokeReading{
          smokeAlarm
          batteryLevel
        }`,
        details: [
          {
            classification: SensorClassification.NONE,
            properties: null,
            yAxisLabels: [],
            datasetLabels: {
              smokeAlarm: '',
            },
            colors: defaultChartColors,
            stacked: false,
          },
        ],
      },
      {
        type: SensorTypes.DOOR,
        properties: `... on doorReading{
          lastDoorStatus
          batteryLevel
        }`,
        details: [
          {
            classification: SensorClassification.NONE,
            properties: null,
            yAxisLabels: [],
            datasetLabels: {
              lastDoorStatus: '',
            },
            colors: defaultChartColors,
            stacked: false,
          },
        ],
      },
      {
        type: SensorTypes.PUSH,
        properties: `... on pushReading{
          lastPushStatus
          batteryLevel
          pressedTime
        }`,
        details: [
          {
            classification: SensorClassification.NONE,
            properties: null,
            yAxisLabels: [],
            datasetLabels: {
              lastPushStatus: '',
            },
            colors: defaultChartColors,
            stacked: false,
          },
        ],
      },
      {
        type: SensorTypes.GPS,
        properties: `... on gpsReading{
          gpsStatus
          batteryLevel
        }`,
        details: [
          {
            classification: SensorClassification.NONE,
            properties: null,
            yAxisLabels: [],
            datasetLabels: {},
            colors: defaultChartColors,
            stacked: false,
          },
        ],
      },
      {
        type: SensorTypes.WATER,
        properties: `... on waterReading {
          waterType
          waterConsumption
        }`,
        details: [
          {
            classification: SensorClassification.NONE,
            properties: null,
            yAxisLabels: [this.generateLabel(0, 'left', datasetLabels.water, 0.3)],
            datasetLabels: {
              waterConsumption: datasetLabels.water,
            },
            colors: defaultChartColors,
            stacked: false,
          },
        ],
      },
      {
        type: SensorTypes.HEAT,
        properties: `... on heatReading {
          heatConsumption
        }`,
        details: [
          {
            classification: SensorClassification.NONE,
            properties: null,
            yAxisLabels: [this.generateLabel(0, 'left', datasetLabels.heat, 0.3)],
            datasetLabels: {
              heatConsumption: datasetLabels.heat,
            },
            colors: defaultChartColors,
            stacked: false,
          },
        ],
      },
      {
        type: SensorTypes.GAS,
        properties: ``,
        details: [
          {
            classification: GasSensorClassification.ESTIMATED,
            properties: `gas_consumption_obis_converted_estimation_1`,
            yAxisLabels: [this.generateLabel(0, 'left', datasetLabels.gasEstimated, 0.3)],
            datasetLabels: {
              gas_consumption_obis_converted_estimation_1: datasetLabels.gasEstimated,
            },
            colors: defaultChartColors,
            stacked: false,
          },
          {
            classification: GasSensorClassification.CALCULATED,
            properties: `gas_consumption_obis_raw_1`,
            yAxisLabels: [this.generateLabel(0, 'left', datasetLabels.gasCalculated, 0.3)],
            datasetLabels: {
              gas_consumption_obis_raw_1: datasetLabels.gasCalculated,
            },
            colors: defaultChartColors,
            stacked: false,
          },
        ],
      },
      {
        type: SensorTypes.ELECTRICITY,
        properties: ``,
        details: [
          {
            classification: SensorClassification.NONE,
            properties: `
              el_consumption_total_1
              el_consumption_obis_180_1`,
            yAxisLabels: [this.generateLabel(0, 'left', datasetLabels.electricity, 0.3)],
            datasetLabels: {
              el_consumption_total_1: datasetLabels.electricity,
            },
            colors: defaultChartColors,
            stacked: false,
          },
        ],
      },
      {
        type: SensorTypes.DOGTOILET,
        properties: `... on dogToiletReading{
          dogBags
          dogTrashCan
          batteryLevel
        }`,
        details: [
          {
            classification: SensorClassification.NONE,
            properties: null,
            yAxisLabels: [this.generateLabel(0, 'left', datasetLabels.dogToilet)],
            datasetLabels: {
              dogBags: datasetLabels.dogBags,
              dogTrashCan: datasetLabels.dogTrashCan,
            },
            colors: defaultChartColors,
            stacked: false,
          },
        ],
      },
      {
        type: SensorTypes.SOILMOISTURE,
        properties: `... on moistureReading{
          moisture
          temperature
          batteryLevel
        }`,
        interfaces: `qualityIndices
          currentQuality
          criticalQualityIndex`,
        details: [
          {
            classification: SensorClassification.NONE,
            properties: null,
            yAxisLabels: [this.generateLabel(0, 'left', `${datasetLabels.moisture} (${units.permittivity})`)],
            datasetLabels: {
              moisture: datasetLabels.moisture,
            },
            colors: defaultChartColors,
            stacked: false,
          },
        ],
      },
    ];
  }

  generateLabel(id: number, position: string, labelString: string, gridlineWidth?: number): IYAxisLabels {
    const label: IYAxisLabels = {
      id,
      position,
      ticks: {
        beginAtZero: true,
      },
      title: {
        display: true,
        text: labelString,
      },
    };

    if (gridlineWidth) {
      label.grid = {
        lineWidth: gridlineWidth,
      };
    }

    return label;
  }

  public getCurrentSpeedLimit(): number {
    if (isNil(this.selectedSensor)) {
      return 50;
    }
    const speedLimitJSON: SpeedLimitDto | undefined = this.selectedSensor.metaData.speedLimitJSON;
    return isNil(speedLimitJSON) || speedLimitJSON.isDefault ? defaultSpeedLimit.value : speedLimitJSON.value;
  }
}

interface IYAxisLabels {
  id: number;
  position: string;
  ticks: {
    beginAtZero: boolean;
  };
  title: {
    display: boolean;
    text: string;
  };
  grid?: {
    lineWidth: number;
  };
}

interface ISettings {
  type: SensorTypes;
  properties: string;
  interfaces?: string;
  details: Array<{
    classification: string;
    properties: string | null;
    yAxisLabels: IYAxisLabels[];
    datasetLabels: {
      [key: string]: string;
    };
    colors: string;
    stacked: boolean;
  }>;
}
